import axios, { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GridDataStateChangeEvent,
  GridRowDoubleClickEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { DataResult, State } from "@progress/kendo-data-query";
import { faCalculator, faPencil, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { Grid, useTheme } from "@mui/material";
import { GridColumn } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { toast } from "react-toastify";
import { trackPromise } from "react-promise-tracker";
import { InitialState } from "../../../Grid/InitialDataState";
import { GridSelectableRowsProps } from "../../../Grid/GridSelectableRowsProps";
import PdsrOverviewGridColumns from "./PdsrOverviewGridColumns";
import StyledKendoGrid from "../../../Grid/StyledKendoGrid";
import useSavedState from "../../../Grid/UseSavedState";
import { GridDataLoader } from "../../../Grid/GridDataLoader";
import { OnRowClickType } from "../../../Layout/Grid/GridRowClickProps";
import UploadDialog from "../../../PdsrUpload/UploadDialog";
import { pdsrUrls, returnNetworkUrls } from "../../../URL/ApplicationUrls";
import PageHeader from "../../../Layout/Header/PageHeader";
import { combinedUrls } from "../../../URL/functions";
import { KENDO_BUTTON_CLASSES } from "../../../Layout/Buttons/KendoButtons";
import { IconWithRightMargin } from "../../../Layout/Icons/IconWithRightMargin";
import { UppercaseButton } from "../../../Layout/Buttons/UppercaseButton";
import MdmQuickFilter from "../../../Layout/Filter/MdmQuickFilter";
import MdmRelationQuickFilter from "../../Filter/MdmRelationQuickFilter";
import { PDSR_REGEX, PSR_REGEX } from "../../Filter/RelationRegExp";
import { lastUpdatedTimeStamp } from "../../lastUpdatedTimeStamp";
import StatePersistenceButtons from "../../../Grid/StatePersistenceButtons";
import { PdsrOverviewItem } from "./PdsrOverviewItem";
import { GRID_SELECTED_FIELD } from "../../../Grid/Selectable/GridSelection";
import { useCreateProjectFromPdsrs } from "./Hooks/useCreateProjectFromPdsrs";
import { isColumnFilterActive } from "../../../Grid/Extensions/FilterStatusHelper";
import { DefaultOverviewFilter } from "../../Filter/DefaultOverviewFilter";
import { PdsrStateType } from "../EditPdsr/PdsrState/PdsrStateType";
import { useDeletePdsrs } from "./Hooks/useDeletePdsrs";
import { removeFromDataResult } from "../../../Grid/RemoveFromDataResult";
import { confirm } from "../../../Layout/Dialog/ConfirmDialog";
import { useOverviewLastUpdated } from "../../../Axios/useOverviewLastUpdated";

import {
  showNotSelfClosingActionFailureToast,
  showNotSelfClosingActionStartToast,
  showNotSelfClosingActionSuccessToast,
  ToastId,
} from "../../NotificationForActionButton";
import { DATA_ITEM_KEY, gridPagerDefaultSettings } from "../../../Grid/GridConstants";
import HttpStatusCode from "../../../Utils/HttpConstants";
import GridContentSpinner from "../../../Grid/GridContentSpinner";
import fetchGridData from "../../../Grid/Functions/fetchGridData";
import RenderAuthorized from "../../../Auth/RenderAuthorized";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";
import { getPdsrIdOriginTable } from "../../../Grid/gridUtils";
import { isArrayUndefinedOrEmpty } from "../../../Utils/arrays";
import { pdsrOverviewToolbarMenu } from "./PdsrOverviewToolbarMenu";
import { MdmApiError, mdmApiErrorMessageOrFallbackMessage } from "../../../Errors/MdmApiError";

const PDSROVERVIEW_GRIDDATASTATE = "PdsrOverviewGrid_DataState";

export default function PdsrOverviewGrid() {
  const navigate = useNavigate();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const theme = useTheme();

  const { mutate: deletePdsrs } = useDeletePdsrs();
  const { data: lastUpdated, refetch: refetchLastUpdated } = useOverviewLastUpdated(
    pdsrUrls.overviewLastUpdated,
    "pdsrs"
  );

  const [dataResult, setDataResult] = useState<DataResult>({
    data: [],
    total: 0,
  });
  const [recalculationTriggered, setRecalculationTriggered] = useState<boolean>(false);

  const toastId: ToastId = useRef(null);

  const onRowClick: OnRowClickType = (dataItem) => {
    const id = dataItem.Id;
    setSelectedState({ [id]: true });
    setSelectedIds([...selectedIds, id]);
  };

  const initialDataState: State = {
    ...InitialState,
    filter: DefaultOverviewFilter,
  };

  const [dataState, setDataState] = useSavedState<State>(
    PDSROVERVIEW_GRIDDATASTATE,
    initialDataState
  );

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const handleRowDoubleClick = (e: GridRowDoubleClickEvent) => {
    const pdsr = e.dataItem as PdsrOverviewItem;
    navigate(returnNetworkUrls.editPdsr(pdsr.PdsrIdOriginTable));
  };

  const onDataReceived = (pdsrs: DataResult) => {
    setDataResult(pdsrs);
  };

  const [selectedState, setSelectedState] = useState<{
    [id: number]: boolean | number[];
  }>({});
  const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
  const [allItemsSelected, setAllItemsSelected] = useState<boolean>(false);
  const selectableRowsProps = GridSelectableRowsProps<PdsrOverviewItem>({
    selectedState,
    setSelectedState,
    setSelectedIds,
    dataItemKey: DATA_ITEM_KEY,
    setAllItemsSelected,
  });

  const { mutate: createProjectFromPdsrs } = useCreateProjectFromPdsrs();
  const createProject = async () => {
    const originTableIds: number[] = await getPdsrIdOriginTable({
      selectedIds,
      currentDatastate: dataState,
      tableDataResult: dataResult,
      allItemsSelected,
    });
    if (isArrayUndefinedOrEmpty(originTableIds)) return;
    createProjectFromPdsrs(originTableIds);
  };

  const GridRef = useRef<any>();
  const fetchDataUrl = combinedUrls(pdsrUrls.base.apiUrl, "KendoGrid");

  const columns = PdsrOverviewGridColumns({
    dataResult,
    selectedState,
    onRowClick,
  });

  const getDeletablePdsrIds = () => {
    return dataResult.data
      .filter((it) => selectedIds.includes(it.Id))
      .filter((it) => isPdsrDeletable(it));
  };

  const isPdsrDeletable = (pdsr: PdsrOverviewItem) => {
    return (
      pdsr?.State === PdsrStateType.Expired &&
      (pdsr?.Classification === "LTL" ||
        pdsr?.Classification === "Out of Scope" ||
        pdsr?.Classification === "-")
    );
  };

  async function deletePdsrWithDialog(deletablePdsrs: PdsrOverviewItem[]) {
    if (deletablePdsrs.length !== selectedIds.length) {
      toast.error(
        "Not all PDSR(s) are deletable. Only expired PDSRs without Classification or with Classification LTL or Out-Of-Scope can be deleted. Please check selected PDSR(s)"
      );
    } else {
      const isConfirmed = await confirm(
        `Do you really want to delete PDSR(s) ${deletablePdsrs
          .map((pdsr) => pdsr.Pdsr)
          .join(`, `)}?`
      );
      if (!isConfirmed) {
        return;
      }
      deletePdsrs(deletablePdsrs.map((pdsr) => pdsr.PdsrIdOriginTable));
      removeFromDataResult(selectedIds, dataResult, setDataResult);
    }
  }

  const triggerCalculation = () => {
    setRecalculationTriggered(true);
    toastId.current = showNotSelfClosingActionStartToast("Calculating PDSRs");
    axios
      .get(pdsrUrls.triggerCalculation)
      .then((response) => {
        showNotSelfClosingActionSuccessToast(toastId, "PDSR calculation successful");
        if (response.status === HttpStatusCode.OK) {
          trackPromise(refetchLastUpdated());
          trackPromise<DataResult>(fetchGridData(fetchDataUrl, dataState)).then((newDataResult) =>
            setDataResult(newDataResult)
          );
        }
      })
      .catch((error: AxiosError<MdmApiError>) =>
        showNotSelfClosingActionFailureToast(
          toastId,
          mdmApiErrorMessageOrFallbackMessage(error, "Error while calculating PDSRs")
        )
      )
      .finally(() => setRecalculationTriggered(false));
  };

  const pdsrExportMenu = pdsrOverviewToolbarMenu();
  const exportButton = pdsrExportMenu(dataState, returnNetworkUrls.reportOverviewData);

  return (
    <div>
      <PageHeader headerText="PDSR Overview">{lastUpdatedTimeStamp(lastUpdated)}</PageHeader>
      <Grid key="customFilterPdsr" container justifyContent="left" ml={-1} spacing={0.5}>
        <Grid item key="routeNumberFilter">
          <MdmQuickFilter
            currentDataState={dataState}
            setDataState={setDataState}
            findText="Route Number"
            filterFields={[
              "RouteIntoFirstOdc",
              "RouteIntoSecondOdc",
              "RouteInto3rdOdc",
              "DeliveryToSupplierRoute",
            ]}
          />
        </Grid>
        <Grid item key="odcFilter">
          <MdmQuickFilter
            currentDataState={dataState}
            setDataState={setDataState}
            findText="ODC"
            filterFields={["FirstOdc", "SecondOdc", "ThirdOdc"]}
          />
        </Grid>
        <Grid item key="trainFerryFilter">
          <MdmQuickFilter
            currentDataState={dataState}
            setDataState={setDataState}
            findText="Train/Ferry GSDB"
            filterFields={[
              "DeliveryToSupplierTrainFerry",
              "FirstOdcTrainFerry",
              "SecondOdcTrainFerry",
              "ThirdOdcTrainFerry",
            ]}
          />
        </Grid>
        <Grid item key="pdsrFilter">
          <MdmRelationQuickFilter
            currentDataState={dataState}
            findText="PSR / PDSR"
            triggerFiltering={setDataState}
            relationMatchers={[
              {
                relation: "PSR",
                regex: PSR_REGEX,
              },
              {
                relation: "PDSR",
                regex: PDSR_REGEX,
              },
            ]}
          />
        </Grid>
      </Grid>
      <UploadDialog
        isOpen={isUploadDialogOpen}
        handleCancelButtonClick={() => setIsUploadDialogOpen(false)}
        handleOkButtonClick={() => setIsUploadDialogOpen(false)}
      />
      <StyledKendoGrid
        ref={GridRef}
        muiTheme={theme}
        height="65vh"
        sortable
        filterable
        pageable={{
          ...gridPagerDefaultSettings,
        }}
        resizable
        scrollable="scrollable"
        {...dataState}
        data={{
          data: dataResult.data.map((item) => ({
            ...item,
            [GRID_SELECTED_FIELD]: selectedState[item.Id],
          })),
          total: dataResult.total,
        }}
        onDataStateChange={dataStateChange}
        onRowDoubleClick={handleRowDoubleClick}
        rowHeight={50}
        {...selectableRowsProps}
      >
        <GridToolbar>
          <StatePersistenceButtons
            localStorageKey={PDSROVERVIEW_GRIDDATASTATE}
            initialState={initialDataState}
            dataState={dataState}
            resetButtonText="RESET FILTER"
            saveButtonText="SAVE FILTER"
            align="left"
          />
          <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireCalculationTriggerRole}>
            <UppercaseButton
              type="button"
              title="Recalculate Overview"
              className={KENDO_BUTTON_CLASSES}
              onClick={triggerCalculation}
              disabled={recalculationTriggered}
            >
              <IconWithRightMargin icon={faCalculator} />
              Recalculate Overview
            </UppercaseButton>
          </RenderAuthorized>
          <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
            <UppercaseButton
              type="button"
              title="Delete PDSR"
              className={KENDO_BUTTON_CLASSES}
              onClick={() => deletePdsrWithDialog(getDeletablePdsrIds())}
              disabled={selectedIds.length === 0}
            >
              <IconWithRightMargin icon={faTrash} />
              DELETE PDSR
            </UppercaseButton>
            <UppercaseButton
              type="button"
              title="CREATE PROJECT"
              className={KENDO_BUTTON_CLASSES}
              onClick={createProject}
              disabled={selectedIds.length === 0}
            >
              <IconWithRightMargin icon={faPencil} />
              CREATE PROJECT
            </UppercaseButton>
            <UppercaseButton
              type="button"
              title="PDSR-Upload"
              className={KENDO_BUTTON_CLASSES}
              onClick={() => setIsUploadDialogOpen(true)}
            >
              <IconWithRightMargin icon={faUpload} />
              PDSR-UPLOAD
            </UppercaseButton>
          </RenderAuthorized>
          {exportButton}
        </GridToolbar>
        {columns.map((column) => (
          <GridColumn
            key={column.key}
            {...column.props}
            headerClassName={
              isColumnFilterActive(column.props.field, dataState) ? "filterActive" : ""
            }
          />
        ))}
      </StyledKendoGrid>
      <GridContentSpinner />
      <GridDataLoader url={fetchDataUrl} dataState={dataState} onDataReceived={onDataReceived} />
    </div>
  );
}
